/**
 * services/_demo/seed.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Faithful demo dataset (restaurant / fashion / construction) ported from the
 * design prototype. It powers (a) the seed fallback when the live API is
 * unreachable and (b) the data the vertical switcher swaps between. In
 * production the live API answers first and this is never read.
 *
 * Everything here is produced in the app's NORMALIZED schema shapes, so the
 * services can return it directly with zero per-screen branching.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Rule, RuleStatus, RuleSource, CategoryOption } from '../../schema/rules/rule';
import { Proposal } from '../../schema/proposal';
import { PresetSummary, PresetDetail, PresetRuleManifest } from '../../schema/preset';
import { Capabilities } from '../../schema/capability';
import { Settings } from '../../schema/settings';
import { Stats, RuleStats, ByChannel } from '../../schema/stats';
import { trendArr } from '../../lib/spark';
import type { DemoVertical } from './verticalStore';

/* ── Catalog items (preview candidate pools) ───────────────────────────────── */
export interface SeedItem {
  id: string;
  name: string;
  cat: string;
  diet: '' | 'veg' | 'non_veg';
  price: number;
  avail: boolean;
  attach: number;
  pop: number;
}

const CATALOG: Record<DemoVertical, SeedItem[]> = {
  restaurant: [
    { id: 'itm_butter_chk', name: 'Butter Chicken', cat: 'mains', diet: 'non_veg', price: 320, avail: true, attach: 0.34, pop: 96 },
    { id: 'itm_paneer_tm', name: 'Paneer Tikka Masala', cat: 'mains', diet: 'veg', price: 280, avail: true, attach: 0.3, pop: 88 },
    { id: 'itm_dosa', name: 'Masala Dosa', cat: 'mains', diet: 'veg', price: 140, avail: true, attach: 0.21, pop: 74 },
    { id: 'itm_coke', name: 'Coca-Cola', cat: 'beverages', diet: 'veg', price: 60, avail: true, attach: 0.41, pop: 91 },
    { id: 'itm_lime', name: 'Fresh Lime Soda', cat: 'beverages', diet: 'veg', price: 80, avail: true, attach: 0.33, pop: 80 },
    { id: 'itm_chaas', name: 'Masala Chaas', cat: 'beverages', diet: 'veg', price: 50, avail: true, attach: 0.27, pop: 64 },
    { id: 'itm_lassi', name: 'Mango Lassi', cat: 'beverages', diet: 'veg', price: 90, avail: true, attach: 0.38, pop: 83 },
    { id: 'itm_coldcoffee', name: 'Cold Coffee', cat: 'beverages', diet: 'veg', price: 120, avail: false, attach: 0.29, pop: 70 },
    { id: 'itm_gulab', name: 'Gulab Jamun', cat: 'desserts', diet: 'veg', price: 110, avail: true, attach: 0.24, pop: 77 },
    { id: 'itm_garlic', name: 'Garlic Bread', cat: 'sides', diet: 'veg', price: 130, avail: true, attach: 0.4, pop: 72 },
    { id: 'itm_papad', name: 'Roasted Papad', cat: 'sides', diet: 'veg', price: 40, avail: true, attach: 0.36, pop: 60 },
  ],
  fashion: [
    { id: 'fa_dress', name: 'Silk Wrap Dress', cat: 'dresses', diet: '', price: 4200, avail: true, attach: 0.3, pop: 92 },
    { id: 'fa_shirt', name: 'Linen Shirt', cat: 'tops', diet: '', price: 1800, avail: true, attach: 0.26, pop: 80 },
    { id: 'fa_tee', name: 'Cotton Tee', cat: 'tops', diet: '', price: 800, avail: true, attach: 0.22, pop: 70 },
    { id: 'fa_neck', name: 'Statement Necklace', cat: 'accessories', diet: '', price: 950, avail: true, attach: 0.42, pop: 88 },
    { id: 'fa_belt', name: 'Leather Belt', cat: 'accessories', diet: '', price: 1200, avail: true, attach: 0.31, pop: 74 },
    { id: 'fa_scarf', name: 'Silk Scarf', cat: 'accessories', diet: '', price: 700, avail: true, attach: 0.36, pop: 79 },
    { id: 'fa_clutch', name: 'Evening Clutch', cat: 'accessories', diet: '', price: 1600, avail: false, attach: 0.28, pop: 66 },
    { id: 'fa_sandals', name: 'Heeled Sandals', cat: 'footwear', diet: '', price: 2800, avail: true, attach: 0.25, pop: 72 },
    { id: 'fa_care', name: 'Fabric Care Kit', cat: 'care', diet: '', price: 350, avail: true, attach: 0.38, pop: 60 },
  ],
  construction: [
    { id: 'bd_kitchen', name: 'Modular Kitchen Unit', cat: 'fixtures', diet: '', price: 85000, avail: true, attach: 0.3, pop: 90 },
    { id: 'bd_hardware', name: 'Premium Hardware Set', cat: 'fixtures', diet: '', price: 8000, avail: true, attach: 0.34, pop: 76 },
    { id: 'bd_light', name: 'Smart Lighting Kit', cat: 'fixtures', diet: '', price: 15000, avail: false, attach: 0.26, pop: 64 },
    { id: 'bd_quartz', name: 'Quartz Countertop', cat: 'finishes', diet: '', price: 32000, avail: true, attach: 0.28, pop: 82 },
    { id: 'bd_consult', name: 'Design Consultation', cat: 'services', diet: '', price: 5000, avail: true, attach: 0.44, pop: 88 },
    { id: 'bd_install', name: 'Installation Service', cat: 'services', diet: '', price: 12000, avail: true, attach: 0.5, pop: 85 },
    { id: 'bd_warranty', name: 'Extended Warranty', cat: 'protection', diet: '', price: 4500, avail: true, attach: 0.35, pop: 70 },
    { id: 'bd_waterpr', name: 'Waterproofing', cat: 'protection', diet: '', price: 6000, avail: true, attach: 0.3, pop: 68 },
  ],
};

/* ── Category vocabularies + builder defaults per vertical ─────────────────── */
interface VerticalConfig {
  cats: CategoryOption[];
  catLabel: Record<string, string>;
  defaults: { triggerCat: string; excludeCat: string; fromValue: string };
  compatLabel: string;
  phrasing: string;
  currency: string;
}

export const VERTICAL_CONFIG: Record<DemoVertical, VerticalConfig> = {
  restaurant: {
    cats: [
      { value: 'mains', label: 'mains' },
      { value: 'beverages', label: 'beverages' },
      { value: 'desserts', label: 'desserts' },
      { value: 'sides', label: 'sides' },
    ],
    catLabel: { mains: 'mains', beverages: 'a beverage', desserts: 'a dessert', sides: 'a side' },
    defaults: { triggerCat: 'mains', excludeCat: 'beverages', fromValue: 'beverages' },
    compatLabel: 'Respect dietary tags as a hard filter (veg never gets non-veg)',
    phrasing: 'Add a {item_name} with that?',
    currency: '₹',
  },
  fashion: {
    cats: [
      { value: 'dresses', label: 'a dress' },
      { value: 'tops', label: 'a top' },
      { value: 'accessories', label: 'an accessory' },
      { value: 'footwear', label: 'footwear' },
      { value: 'care', label: 'a care item' },
    ],
    catLabel: { dresses: 'a dress', tops: 'a top', accessories: 'an accessory', footwear: 'footwear', care: 'a care item' },
    defaults: { triggerCat: 'dresses', excludeCat: 'accessories', fromValue: 'accessories' },
    compatLabel: 'Respect size & style tags as a hard filter (no mismatched fit)',
    phrasing: 'Pair it with a {item_name}?',
    currency: '₹',
  },
  construction: {
    cats: [
      { value: 'fixtures', label: 'a fixture' },
      { value: 'finishes', label: 'a finish' },
      { value: 'services', label: 'a service' },
      { value: 'protection', label: 'protection' },
    ],
    catLabel: { fixtures: 'a fixture', finishes: 'a finish', services: 'a service', protection: 'protection' },
    defaults: { triggerCat: 'fixtures', excludeCat: 'services', fromValue: 'services' },
    compatLabel: 'Respect spec & dimension tags as a hard filter (no wrong-size SKU)',
    phrasing: 'Add {item_name} to your project?',
    currency: '₹',
  },
};

/* ── Seed rules (raw counts kept so stats can be derived) ───────────────────── */
interface SeedRule {
  name: string;
  motion: string;
  status: RuleStatus;
  source: RuleSource;
  priority: number;
  trigger: string;
  fromText: string;
  select: string;
  accepted: number;
  declined: number;
  ignored: number;
  du: number;
  dormant: boolean;
  cold: boolean;
}

const rk = (
  name: string,
  motion: string,
  status: RuleStatus,
  source: RuleSource,
  priority: number,
  trigger: string,
  fromText: string,
  select: string,
  accepted: number,
  declined: number,
  ignored = 0,
  du = 0,
  dormant = false,
  cold = false,
): SeedRule => ({ name, motion, status, source, priority, trigger, fromText, select, accepted, declined, ignored, du, dormant, cold });

const SEED_RULES: Record<DemoVertical, SeedRule[]> = {
  restaurant: [
    rk('Add a drink with the mains', 'complement', 'active', 'ai_proposed', 100, 'Cart has a main, no beverage', 'a beverage', 'attach_rate', 420, 815, 180, 95),
    rk('Garlic bread with pasta or mains', 'bundle_fill', 'active', 'manual', 88, 'Cart has a main, no side', 'a side', 'attach_rate', 512, 742, 140, 60),
    rk('Upsize the fries / portion', 'size_up', 'active', 'manual', 90, 'A regular-size item was added', 'the large size', 'price_up_nearest', 288, 720, 90, 40),
    rk('Suggest a dessert to finish', 'complement', 'active', 'ai_proposed', 80, 'Cart has a main and a drink', 'a dessert', 'curated_order', 196, 820, 210, 120),
    rk('Mocktail over a soft drink', 'premium_variant', 'active', 'ai_proposed', 75, 'A soft drink was added', 'a fresh mocktail', 'curated_order', 9, 7, 4, 3, false, true),
    rk('Combo nudge near ₹400', 'threshold_nudge', 'active', 'manual', 70, 'Cart total ≥ ₹350, under a combo', 'a combo meal', 'curated_order', 141, 498, 260, 140, true),
    rk('Upgrade to the premium thali', 'tier_up', 'draft', 'manual', 100, 'A regular thali was added', 'the premium thali', 'price_up_nearest', 0, 0, 0, 0),
    rk('Extra dip with the starters', 'accessory', 'deactivated', 'manual', 60, 'A starter was added', 'a dip', 'attach_rate', 88, 640, 90, 40),
  ],
  fashion: [
    rk('Add accessories to a dress', 'complement', 'active', 'ai_proposed', 100, 'Cart has a dress, no accessory', 'an accessory', 'attach_rate', 380, 720, 150, 80),
    rk('Fabric care kit with silk items', 'accessory', 'active', 'ai_proposed', 92, 'A silk item was added', 'a care kit', 'attach_rate', 460, 640, 90, 40),
    rk('Upgrade to premium fabric', 'tier_up', 'active', 'manual', 95, 'A standard-fabric item was added', 'the premium fabric', 'price_up_nearest', 150, 520, 70, 30),
    rk('Pair footwear with the outfit', 'complement', 'active', 'manual', 85, 'Cart has apparel, no footwear', 'footwear', 'attach_rate', 210, 690, 120, 60),
    rk('Complete the look bundle', 'bundle_fill', 'active', 'manual', 80, 'Cart has a top, no bottom', 'a matching piece', 'curated_order', 12, 9, 5, 4, false, true),
    rk('Belt with trousers', 'accessory', 'deactivated', 'manual', 60, 'Trousers added', 'a belt', 'attach_rate', 70, 540, 60, 30),
    rk('Premium tailoring add-on', 'service_attach', 'draft', 'manual', 100, 'Any garment added', 'alterations', 'curated_order', 0, 0, 0, 0),
  ],
  construction: [
    rk('Design consult with kitchen orders', 'service_attach', 'active', 'ai_proposed', 100, 'A modular unit added, no consult', 'a design consultation', 'attach_rate', 88, 140, 30, 20),
    rk('Installation with fixtures', 'service_attach', 'active', 'manual', 95, 'A fixture added, no install', 'installation', 'attach_rate', 120, 150, 25, 15),
    rk('Extended warranty on appliances', 'protection', 'active', 'ai_proposed', 85, 'An appliance added', 'an extended warranty', 'attach_rate', 60, 180, 40, 25),
    rk('Upgrade to premium hardware', 'tier_up', 'active', 'manual', 90, 'Standard hardware added', 'premium hardware', 'price_up_nearest', 45, 160, 20, 12),
    rk('Waterproofing with bathroom fittings', 'protection', 'active', 'manual', 80, 'A bathroom fitting added', 'waterproofing', 'curated_order', 6, 5, 3, 2, false, true),
    rk('Smart lighting bundle', 'bundle_fill', 'draft', 'manual', 100, 'A room package added', 'smart lighting', 'curated_order', 0, 0, 0, 0),
    rk('Premium finish upgrade', 'premium_variant', 'deactivated', 'manual', 60, 'Standard finish added', 'a premium finish', 'price_up_nearest', 30, 210, 18, 10),
  ],
};

/* ── Proposals ──────────────────────────────────────────────────────────────── */
const PROPOSALS: Record<DemoVertical, Proposal[]> = {
  restaurant: [
    { id: 'pr1', motion: 'complement', confidence: 0.92, intent: 'Suggest a raita when someone orders biryani but hasn’t added one.', rationale: 'Biryani + raita is your strongest unsold pairing — 87% of biryani orders skip it.', from: { category: 'raita' }, select: 'curated_order', maxPriceDelta: 40 },
    { id: 'pr2', motion: 'accessory', confidence: 0.85, intent: 'Add roasted papad with any thali order.', rationale: 'Papad is a cheap, near-universal add to thalis; low price-delta keeps it easy to say yes.', from: { category: 'sides' }, select: 'attach_rate', maxPriceDelta: 25 },
    { id: 'pr3', motion: 'size_up', confidence: 0.78, intent: 'Offer the large portion when a regular curry is added.', rationale: 'Margin grows faster than cost on the large size; nearest price-up keeps the jump gentle.', from: { relation: 'next_size' }, select: 'price_up_nearest', maxPriceDelta: 60 },
    { id: 'pr4', motion: 'bundle_fill', confidence: 0.71, intent: 'Complete the meal with gulab jamun after a main + bread.', rationale: 'Dessert attach lifts ticket without cannibalising; curated order until it has signal.', from: { category: 'desserts' }, select: 'curated_order', maxPriceDelta: 50 },
    { id: 'pr5', motion: 'tier_up', confidence: 0.64, intent: 'Upgrade soft drinks to a fresh lime soda.', rationale: 'Premium-variant swap with a small delta; confidence is lower so review carefully.', from: { category: 'beverages' }, select: 'curated_order', maxPriceDelta: 45 },
  ],
  fashion: [
    { id: 'pf1', motion: 'complement', confidence: 0.9, intent: 'Suggest a silk scarf with any dress.', rationale: 'Scarf + dress is your top unsold pairing — a light, easy add that lifts ticket.', from: { category: 'accessories' }, select: 'curated_order', maxPriceDelta: 40 },
    { id: 'pf2', motion: 'accessory', confidence: 0.86, intent: 'Add a fabric care kit to silk purchases.', rationale: 'Protects the garment and attaches cheaply; high accept on premium fabrics.', from: { category: 'care' }, select: 'attach_rate', maxPriceDelta: 25 },
    { id: 'pf3', motion: 'service_attach', confidence: 0.74, intent: 'Offer alterations / tailoring at checkout.', rationale: 'High-margin service attach on fitted garments; review the fit policy.', from: { category: 'services' }, select: 'curated_order', maxPriceDelta: 60 },
    { id: 'pf4', motion: 'tier_up', confidence: 0.68, intent: 'Upgrade to the premium-fabric variant.', rationale: 'Gentle price step on standard items; nearest price-up keeps it easy.', from: { relation: 'next_tier' }, select: 'price_up_nearest', maxPriceDelta: 50 },
    { id: 'pf5', motion: 'bundle_fill', confidence: 0.71, intent: 'Complete the look with matching footwear.', rationale: 'Outfit completion bundle; curated order until it has signal.', from: { category: 'footwear' }, select: 'curated_order', maxPriceDelta: 70 },
  ],
  construction: [
    { id: 'pc1', motion: 'service_attach', confidence: 0.91, intent: 'Add a design consultation to large fit-out orders.', rationale: 'Consults convert to bigger projects; strongest attach on high-value carts.', from: { category: 'services' }, select: 'attach_rate', maxPriceDelta: 60 },
    { id: 'pc2', motion: 'service_attach', confidence: 0.88, intent: 'Bundle installation with the modular kitchen.', rationale: 'Installation attach is near-universal on fixtures; protects the build quality.', from: { category: 'services' }, select: 'curated_order', maxPriceDelta: 60 },
    { id: 'pc3', motion: 'protection', confidence: 0.83, intent: 'Offer an extended warranty on appliances.', rationale: 'Low-cost protection attach with healthy margin and high accept.', from: { category: 'protection' }, select: 'attach_rate', maxPriceDelta: 30 },
    { id: 'pc4', motion: 'tier_up', confidence: 0.7, intent: 'Upgrade to a premium-grade countertop.', rationale: 'Nearest price-up on finishes; keeps the jump proportionate.', from: { relation: 'next_tier' }, select: 'price_up_nearest', maxPriceDelta: 50 },
    { id: 'pc5', motion: 'bundle_fill', confidence: 0.66, intent: 'Complete the room with a smart-lighting kit.', rationale: 'Room-completion bundle; curated order until it has signal.', from: { category: 'fixtures' }, select: 'curated_order', maxPriceDelta: 70 },
  ],
};

/* ── Presets + their contents ──────────────────────────────────────────────── */
interface SeedPreset {
  key: string;
  name: string;
  desc: string;
  vertical: string;
}

const PRESETS: Record<DemoVertical, SeedPreset[]> = {
  restaurant: [
    { key: 'restaurant', name: 'Restaurant starter pack', desc: 'Beverage attach, dessert finish, side fill, portion up-size, combo nudge.', vertical: 'sk-restaurant' },
    { key: 'cafe', name: 'Café & bakery pack', desc: 'Pastry with coffee, size-up the latte, loyalty bundle, second-cup nudge.', vertical: 'sk-cafe' },
    { key: 'qsr', name: 'Quick-service (QSR) pack', desc: 'Make-it-a-meal, up-size, dip accessory, dessert, drink swap, combo, loyalty.', vertical: 'sk-qsr' },
    { key: 'hotel', name: 'Hotel F&B pack', desc: 'Room-service drink attach, breakfast up-tier, spa add-on, late-checkout.', vertical: 'sk-hotel' },
  ],
  fashion: [
    { key: 'apparel', name: 'Apparel & accessories pack', desc: 'Accessory attach, care-kit add, fabric up-tier, look-completion bundle.', vertical: 'sk-fashion' },
    { key: 'boutique', name: 'Boutique starter', desc: 'Scarf-with-dress, statement jewellery, premium-fabric upgrade.', vertical: 'sk-fashion' },
    { key: 'footwearp', name: 'Footwear add-on pack', desc: 'Pair shoes with outfits, sock/insole accessory, care add-on.', vertical: 'sk-fashion' },
    { key: 'tailoring', name: 'Tailoring service pack', desc: 'Alterations attach, monogramming, express-stitch up-tier.', vertical: 'sk-fashion' },
  ],
  construction: [
    { key: 'interiors', name: 'Interiors & fit-out pack', desc: 'Design consult, installation attach, warranty, premium-hardware up-tier.', vertical: 'sk-build' },
    { key: 'kitchen', name: 'Modular kitchen pack', desc: 'Consult-with-kitchen, install bundle, countertop up-tier, smart lighting.', vertical: 'sk-build' },
    { key: 'serviceatt', name: 'Service-attach starter', desc: 'Installation and design-consult attach on every fixture order.', vertical: 'sk-build' },
    { key: 'warranty', name: 'Warranty & protection pack', desc: 'Extended warranty, waterproofing, AMC at handover.', vertical: 'sk-build' },
  ],
};

const PRESET_CONTENTS: Record<string, PresetRuleManifest[]> = {
  restaurant: [
    { name: 'Beverage attach on mains', motion: 'complement', trigger_summary: 'Cart has a main, no beverage', select: 'curated_order' },
    { name: 'Dessert to finish', motion: 'complement', trigger_summary: 'Cart has a main and a drink', select: 'curated_order' },
    { name: 'Side fill with mains', motion: 'bundle_fill', trigger_summary: 'Cart has a main, no side', select: 'attach_rate' },
    { name: 'Up-size the portion', motion: 'size_up', trigger_summary: 'A regular-size item was added', select: 'price_up_nearest' },
    { name: 'Combo nudge near threshold', motion: 'threshold_nudge', trigger_summary: 'Cart total just under a combo', select: 'curated_order' },
    { name: 'Premium drink swap', motion: 'premium_variant', trigger_summary: 'A soft drink was added', select: 'curated_order' },
  ],
  cafe: [
    { name: 'Pastry with coffee', motion: 'complement', trigger_summary: 'A coffee added, no pastry', select: 'attach_rate' },
    { name: 'Size-up the latte', motion: 'size_up', trigger_summary: 'A regular latte added', select: 'price_up_nearest' },
    { name: 'Second-cup nudge', motion: 'threshold_nudge', trigger_summary: 'One drink in the cart', select: 'curated_order' },
    { name: 'Loyalty bundle', motion: 'bundle_fill', trigger_summary: 'Three items in the cart', select: 'curated_order' },
    { name: 'Premium bean upgrade', motion: 'tier_up', trigger_summary: 'Standard coffee added', select: 'price_up_nearest' },
  ],
  qsr: [
    { name: 'Make it a meal', motion: 'bundle_fill', trigger_summary: 'A main added, no combo', select: 'curated_order' },
    { name: 'Up-size combo', motion: 'size_up', trigger_summary: 'A regular combo added', select: 'price_up_nearest' },
    { name: 'Dip accessory', motion: 'accessory', trigger_summary: 'A snack/starter added', select: 'attach_rate' },
    { name: 'Dessert add', motion: 'complement', trigger_summary: 'Cart has a main', select: 'curated_order' },
    { name: 'Drink swap', motion: 'premium_variant', trigger_summary: 'A soft drink added', select: 'curated_order' },
    { name: 'Combo nudge', motion: 'threshold_nudge', trigger_summary: 'Cart near a combo price', select: 'curated_order' },
    { name: 'Loyalty add', motion: 'next_best_action', trigger_summary: 'Returning customer', select: 'curated_order' },
  ],
  hotel: [
    { name: 'Room-service drink attach', motion: 'complement', trigger_summary: 'A meal ordered, no beverage', select: 'curated_order' },
    { name: 'Breakfast up-tier', motion: 'tier_up', trigger_summary: 'Standard breakfast added', select: 'price_up_nearest' },
    { name: 'Spa add-on', motion: 'service_attach', trigger_summary: 'A stay with no spa booking', select: 'curated_order' },
    { name: 'Late-checkout offer', motion: 'service_attach', trigger_summary: 'Checkout-day booking', select: 'curated_order' },
  ],
  apparel: [
    { name: 'Accessory with a dress', motion: 'complement', trigger_summary: 'Cart has a dress, no accessory', select: 'attach_rate' },
    { name: 'Fabric care kit add', motion: 'accessory', trigger_summary: 'A silk item was added', select: 'attach_rate' },
    { name: 'Premium fabric up-tier', motion: 'tier_up', trigger_summary: 'A standard-fabric item added', select: 'price_up_nearest' },
    { name: 'Complete the look bundle', motion: 'bundle_fill', trigger_summary: 'Cart has a top, no bottom', select: 'curated_order' },
    { name: 'Pair footwear', motion: 'complement', trigger_summary: 'Apparel in cart, no footwear', select: 'attach_rate' },
    { name: 'Belt accessory', motion: 'accessory', trigger_summary: 'Trousers added', select: 'attach_rate' },
  ],
  boutique: [
    { name: 'Scarf with a dress', motion: 'complement', trigger_summary: 'Cart has a dress', select: 'curated_order' },
    { name: 'Statement jewellery', motion: 'complement', trigger_summary: 'An eveningwear item added', select: 'attach_rate' },
    { name: 'Premium-fabric upgrade', motion: 'tier_up', trigger_summary: 'A standard-fabric item added', select: 'price_up_nearest' },
    { name: 'Fabric care kit', motion: 'accessory', trigger_summary: 'A delicate item added', select: 'curated_order' },
  ],
  footwearp: [
    { name: 'Pair shoes with an outfit', motion: 'complement', trigger_summary: 'Apparel in cart, no footwear', select: 'attach_rate' },
    { name: 'Insole / comfort accessory', motion: 'accessory', trigger_summary: 'Footwear added', select: 'attach_rate' },
    { name: 'Shoe-care add-on', motion: 'accessory', trigger_summary: 'Leather footwear added', select: 'curated_order' },
    { name: 'Premium leather up-tier', motion: 'tier_up', trigger_summary: 'Standard footwear added', select: 'price_up_nearest' },
    { name: 'Matching belt', motion: 'complement', trigger_summary: 'Leather shoes added', select: 'curated_order' },
  ],
  tailoring: [
    { name: 'Alterations attach', motion: 'service_attach', trigger_summary: 'Any fitted garment added', select: 'curated_order' },
    { name: 'Monogramming', motion: 'service_attach', trigger_summary: 'A premium item added', select: 'curated_order' },
    { name: 'Express-stitch up-tier', motion: 'tier_up', trigger_summary: 'Alterations selected', select: 'price_up_nearest' },
  ],
  interiors: [
    { name: 'Design consultation attach', motion: 'service_attach', trigger_summary: 'A modular unit added, no consult', select: 'attach_rate' },
    { name: 'Installation service', motion: 'service_attach', trigger_summary: 'A fixture added, no install', select: 'attach_rate' },
    { name: 'Extended warranty', motion: 'protection', trigger_summary: 'An appliance added', select: 'attach_rate' },
    { name: 'Premium hardware up-tier', motion: 'tier_up', trigger_summary: 'Standard hardware added', select: 'price_up_nearest' },
    { name: 'Waterproofing', motion: 'protection', trigger_summary: 'A bathroom fitting added', select: 'curated_order' },
    { name: 'Smart-lighting bundle', motion: 'bundle_fill', trigger_summary: 'A room package added', select: 'curated_order' },
  ],
  kitchen: [
    { name: 'Consult with the kitchen', motion: 'service_attach', trigger_summary: 'Modular kitchen added', select: 'attach_rate' },
    { name: 'Installation bundle', motion: 'service_attach', trigger_summary: 'A fixture added', select: 'curated_order' },
    { name: 'Countertop up-tier', motion: 'tier_up', trigger_summary: 'Standard countertop added', select: 'price_up_nearest' },
    { name: 'Smart-lighting add', motion: 'bundle_fill', trigger_summary: 'Kitchen package added', select: 'curated_order' },
    { name: 'Appliance warranty', motion: 'protection', trigger_summary: 'An appliance added', select: 'attach_rate' },
  ],
  serviceatt: [
    { name: 'Installation attach', motion: 'service_attach', trigger_summary: 'Any fixture added', select: 'attach_rate' },
    { name: 'Design-consult attach', motion: 'service_attach', trigger_summary: 'A large order in cart', select: 'curated_order' },
    { name: 'AMC at handover', motion: 'service_attach', trigger_summary: 'Project nearing completion', select: 'curated_order' },
  ],
  warranty: [
    { name: 'Extended warranty', motion: 'protection', trigger_summary: 'An appliance added', select: 'attach_rate' },
    { name: 'Waterproofing', motion: 'protection', trigger_summary: 'A wet-area fitting added', select: 'curated_order' },
    { name: 'AMC contract', motion: 'service_attach', trigger_summary: 'Handover stage', select: 'curated_order' },
    { name: 'Damage / pest cover', motion: 'protection', trigger_summary: 'Woodwork added', select: 'curated_order' },
  ],
};

const INSTALLED: Record<DemoVertical, string[]> = {
  restaurant: ['restaurant'],
  fashion: ['apparel'],
  construction: ['interiors'],
};

/* ── Derivations ───────────────────────────────────────────────────────────── */

let idSeq = 0;
const sid = (prefix: string) => `${prefix}_seed_${(idSeq += 1)}`;

function toRule(s: SeedRule): Rule {
  const denom = s.accepted + s.declined;
  const rate = denom ? s.accepted / denom : null;
  return {
    id: sid('ul'),
    name: s.name,
    motion: s.motion,
    status: s.status,
    source: s.source,
    priority: s.priority,
    triggerText: s.trigger,
    fromText: s.fromText,
    select: s.select,
    acceptRate: rate,
    trend: s.status === 'draft' ? null : trendArr(rate),
    cold: s.cold,
    dormant: s.dormant,
    superseded: false,
  };
}

const CAPABILITIES: Capabilities = {
  strategies: [
    { key: 'curated_order', available: true },
    { key: 'attach_rate', available: true },
    { key: 'most_popular', available: true },
    { key: 'price_up_nearest', available: true },
    { key: 'highest_margin', available: false, reason: 'needs cost data' },
    { key: 'similarity_score', available: false, reason: 'needs feature tags' },
  ],
  motions: [],
};

const DEFAULT_SETTINGS: Settings = {
  enabled: true,
  voice_max: 1,
  screen_max: 2,
  ignored_weight: 0,
  min_score_threshold: 0,
  default_select: 'curated_order',
  toggles: {
    tier_up: true,
    size_up: true,
    premium_variant: true,
    complement: true,
    bundle_fill: true,
    accessory: true,
    service_attach: false,
    protection: false,
    volume: true,
    threshold_nudge: true,
    next_best_action: false,
    downsell: false,
  },
  pii_retention_days: 365,
};

const fmtChannel = (delivered: number, accepted: number, declined: number): ByChannel['voice'] => ({
  delivered,
  accepted,
  declined,
  accept_rate: accepted + declined ? accepted / (accepted + declined) : 0,
});

function seededShare(name: string): number {
  const seed = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return 0.3 + (seed % 18) / 100;
}

function ruleStats(s: SeedRule): RuleStats {
  const delivered = s.accepted + s.declined + s.ignored;
  const vShare = seededShare(s.name);
  const vDel = Math.round(delivered * vShare);
  const vAcc = Math.round(s.accepted * vShare * 0.92);
  const vDec = Math.round(s.declined * vShare);
  return {
    rule_id: sid('rs'),
    name: s.name,
    motion: s.motion,
    offered: delivered + s.du,
    delivered,
    accepted: s.accepted,
    declined: s.declined,
    ignored: s.ignored,
    delivery_unknown: s.du,
    accept_rate: s.accepted + s.declined ? s.accepted / (s.accepted + s.declined) : 0,
    delivery_confidence: delivered + s.du ? delivered / (delivered + s.du) : 0,
    by_channel: {
      voice: fmtChannel(vDel, vAcc, vDec),
      screen: fmtChannel(delivered - vDel, s.accepted - vAcc, s.declined - vDec),
    },
    trend: trendArr(s.accepted + s.declined ? s.accepted / (s.accepted + s.declined) : 0) ?? [],
    cold_start: s.cold,
  };
}

function seedStats(rules: SeedRule[]): Stats {
  const live = rules.filter((r) => r.status !== 'draft');
  const A = live.reduce((n, r) => n + r.accepted, 0);
  const D = live.reduce((n, r) => n + r.declined, 0);
  const Ig = live.reduce((n, r) => n + r.ignored, 0);
  const DU = live.reduce((n, r) => n + r.du, 0);
  const delivered = A + D + Ig;
  const vD = Math.round(delivered * 0.34);
  const vA = Math.round(A * 0.29);
  const vDec = Math.round(D * 0.37);
  return {
    aggregate: {
      offered: delivered + DU,
      delivered,
      accepted: A,
      declined: D,
      ignored: Ig,
      delivery_unknown: DU,
      accept_rate: A + D ? A / (A + D) : 0,
      delivery_confidence: delivered + DU ? delivered / (delivered + DU) : 0,
      by_channel: {
        voice: fmtChannel(vD, vA, vDec),
        screen: fmtChannel(delivered - vD, A - vA, D - vDec),
      },
    },
    rules: live.map(ruleStats),
  };
}

/* ── Public seed API (consumed by the service fallbacks) ───────────────────── */
export const Seed = {
  rules: (v: DemoVertical): Rule[] => SEED_RULES[v].map(toRule),
  proposals: (v: DemoVertical): Proposal[] => PROPOSALS[v].map((p) => ({ ...p })),
  stats: (v: DemoVertical): Stats => seedStats(SEED_RULES[v]),
  settings: (): Settings => ({ ...DEFAULT_SETTINGS, toggles: { ...DEFAULT_SETTINGS.toggles } }),
  capabilities: (): Capabilities => ({ strategies: CAPABILITIES.strategies.map((s) => ({ ...s })), motions: [] }),
  presets: (v: DemoVertical): PresetSummary[] =>
    PRESETS[v].map((p) => ({
      key: p.key,
      name: p.name,
      vertical_key: p.vertical,
      rule_count: (PRESET_CONTENTS[p.key] ?? []).length,
      description: p.desc,
    })),
  presetDetail: (key: string): PresetDetail | null => {
    const rules = PRESET_CONTENTS[key];
    if (!rules) return null;
    const meta = Object.values(PRESETS)
      .flat()
      .find((p) => p.key === key);
    return { key, name: meta?.name ?? key, version: 1, rules: rules.map((r) => ({ ...r })) };
  },
  installed: (v: DemoVertical): string[] => [...INSTALLED[v]],
  catalog: (v: DemoVertical): SeedItem[] => CATALOG[v],
  config: (v: DemoVertical): VerticalConfig => VERTICAL_CONFIG[v],
};
